
import { toast } from '@/hooks/use-toast';
import { isSystemBotTokenConfigured } from '@/services/telegram/botTokenService';
import { generateWebhookUrl } from '@/services/telegram/webhookUtils';

// Telegram API base URL
export const TELEGRAM_API = "https://api.telegram.org/bot";

/**
 * Test the connection to the Telegram API
 */
export const testConnection = async (): Promise<{
  success: boolean;
  bot_info?: any;
  message?: string;
}> => {
  try {
    const isConfigured = await isSystemBotTokenConfigured();
    
    if (!isConfigured) {
      return { 
        success: false, 
        message: "Telegram bot token not configured" 
      };
    }
    
    // Since we can't access the token directly, we'll use the edge function to test
    const response = await fetch('/api/telegram-bot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'test-connection' })
    });
    
    const data = await response.json();
    
    if (data.success) {
      return { 
        success: true, 
        bot_info: data.bot_info,
        message: data.message || `Successfully connected to the Telegram API.`
      };
    } else {
      return { 
        success: false, 
        message: data.message || 'Failed to connect to the Telegram API.'
      };
    }
  } catch (error: any) {
    console.error('Failed to test bot connection:', error);
    return {
      success: false,
      message: error.message || 'An unexpected error occurred'
    };
  }
};

/**
 * Get webhook information via edge function
 */
export const getWebhookInfo = async (): Promise<any> => {
  try {
    const isConfigured = await isSystemBotTokenConfigured();
    
    if (!isConfigured) {
      return null;
    }
    
    const response = await fetch('/api/telegram-bot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        action: 'setup-webhook',
        command: 'check'
      })
    });
    
    const data = await response.json();
    
    if (data && data.url !== undefined) {
      return {
        url: data.url,
        is_active: data.url && data.url !== "",
        pending_update_count: data.pending_update_count,
        last_error_date: data.last_error_date,
        last_error_message: data.last_error_message,
        max_connections: data.max_connections
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Failed to get webhook info:', error);
    return null;
  }
};

/**
 * Setup webhook via edge function
 */
export const setupWebhook = async (url?: string): Promise<{
  success: boolean;
  error?: string;
  url?: string;
  is_active?: boolean;
}> => {
  try {
    const isConfigured = await isSystemBotTokenConfigured();
    
    if (!isConfigured) {
      return { 
        success: false, 
        error: "Telegram bot token not configured" 
      };
    }
    
    // Use provided URL or generate the correct one dynamically
    const webhookUrl = url || generateWebhookUrl();
    
    const response = await fetch('/api/telegram-bot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        action: 'setup-webhook',
        command: 'set',
        url: webhookUrl
      })
    });
    
    const data = await response.json();
    
    if (data && data.success) {
      return {
        success: true,
        url: webhookUrl,
        is_active: true
      };
    } else {
      return { 
        success: false, 
        error: data?.error || "Failed to set webhook" 
      };
    }
  } catch (error: any) {
    console.error('Failed to set up webhook:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred'
    };
  }
};

/**
 * Send a message to a chat via edge function
 */
export const sendTelegramMessage = async (
  chatId: number | string,
  message: string,
  parseMode: string = 'Markdown',
  replyMarkup?: any
): Promise<boolean> => {
  try {
    const isConfigured = await isSystemBotTokenConfigured();
    
    if (!isConfigured) {
      console.error("Bot token not configured");
      return false;
    }
    
    const response = await fetch('/api/telegram-bot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'send-message',
        chat_id: chatId,
        message,
        parse_mode: parseMode,
        reply_markup: replyMarkup
      })
    });
    
    const data = await response.json();
    
    if (data && data.success) {
      console.log('Message sent successfully');
      return true;
    } else {
      console.error('Failed to send message:', data);
      return false;
    }
  } catch (error) {
    console.error('Error sending message:', error);
    return false;
  }
};
